import { useCallback, useImperativeHandle, useRef } from 'react'
import 'ag-grid-enterprise'
import {
  ExpandOrCollapseAllEvent,
  FilterChangedEvent,
  ICellRendererParams,
  IsServerSideGroupOpenByDefaultParams,
  RowGroupOpenedEvent,
  SortChangedEvent,
  StoreRefreshedEvent,
} from 'ag-grid-community'
import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-enterprise'
import { useTranslation } from 'react-i18next'
import { IdeyaAgGridTable } from '@/common/ag-grid/ideya-ag-grid-table.tsx'
import {
  DEFAULT_NO_ROWS_TEMPLATE,
  useGridAutoRefresh,
  useGridShared,
} from './hooks/use-grid-setting.ts'
import type { GridRequestContext } from './hooks/use-grid-setting.ts'
import type { SSRMGridProps } from './grid.types.ts'

type RefreshIntent = 'refresh' | 'reload'

interface SSRMRefreshRun {
  epoch: number
  intent: RefreshIntent
  failed: boolean
  requestStarted: boolean
  pendingRoutes: Set<string>
  requestedRoutes: Set<string>
  /** Routes that had zero rows when the refresh started. */
  emptyRoutes: Set<string>
}

function mergeRefreshIntent(
  current: RefreshIntent | null,
  next: RefreshIntent
): RefreshIntent {
  return current === 'reload' || next === 'reload' ? 'reload' : 'refresh'
}

export function SSRMGrid({
  columnDefs,
  storageKey,
  fetchData,
  sortMapping,
  getContextMenuItems,
  getRowStyle,
  rowClassRules,
  onFilterChanged,
  autoRefreshInterval,
  onColumnEvent,
  mobileColumnOrder,
  mobileBreakpoint = 768,
  pageSize = 20,
  height,
  className,
  onRefreshed,
  tutorialColId,
  autoGroupColumnDef,
  getRowId,
  isServerSideGroup,
  getServerSideGroupKey,
  maxBlocksInCache,
  serverSideInitialRowCount,
  onAllGroupsExpandedChange,
  ref,
}: SSRMGridProps) {
  // region Shared Grid Setting

  const { t } = useTranslation()

  const {
    gridApiRef,
    isMobile,
    isInitialLoad,
    setIsInitialLoad,
    beginRequestEpoch,
    getCurrentRequestEpoch,
    isCurrentRequestEpoch,
    showGridLoading,
    finishGridLoading,
    showGridError,
    markBackgroundFetchError,
    handleModelUpdated,
    initialState,
    resolvedColumnDefs,
    defaultColDef,
    handleGridReady,
    handleColumnChanged,
    handleColumnResized,
    handleSortChanged,
  } = useGridShared({
    columnDefs,
    storageKey,
    mobileColumnOrder,
    mobileBreakpoint,
    tutorialColId,
    onColumnEvent,
  })

  // Mutable values read by the stable datasource so its identity never changes;
  // a new datasource identity would make AG Grid rebuild the whole store.
  const errorMessage = t('common.error_loading_data', 'Failed to load data')
  const latestOptionsRef = useRef({
    fetchData,
    sortMapping,
    pageSize,
    onRefreshed,
    errorMessage,
  })
  latestOptionsRef.current = {
    fetchData,
    sortMapping,
    pageSize,
    onRefreshed,
    errorMessage,
  }

  // Only the first (initial) load owns the full-table loading/error overlay;
  // refreshes update existing rows silently.
  const isInitialLoadRef = useRef(true)
  const activeRequestCountRef = useRef(0)
  const activeRefreshRunRef = useRef<SSRMRefreshRun | null>(null)
  const queuedRefreshIntentRef = useRef<RefreshIntent | null>(null)
  const isRefreshStartScheduledRef = useRef(false)
  const startRefreshRef = useRef<(intent: RefreshIntent) => void>(
    () => undefined
  )
  const finishRefreshRef = useRef<() => void>(() => undefined)

  const hasLoadingGridBlocks = useCallback(() => {
    const api = gridApiRef.current
    if (!api) return false

    return Object.values(
      api.getCacheBlockState() as Record<string, { pageStatus?: string }>
    ).some(({ pageStatus }) => pageStatus === 'loading')
  }, [gridApiRef])

  const scheduleQueuedRefresh = useCallback(() => {
    if (
      isRefreshStartScheduledRef.current ||
      activeRefreshRunRef.current ||
      activeRequestCountRef.current > 0 ||
      hasLoadingGridBlocks() ||
      isInitialLoadRef.current ||
      !queuedRefreshIntentRef.current
    )
      return

    isRefreshStartScheduledRef.current = true
    queueMicrotask(() => {
      isRefreshStartScheduledRef.current = false
      const intent = queuedRefreshIntentRef.current
      queuedRefreshIntentRef.current = null
      if (intent) startRefreshRef.current(intent)
    })
  }, [hasLoadingGridBlocks])

  // endregion

  // region Server-Side Datasource

  // Last successful response per block (route + startRow), used to re-serve
  // stale rows to AG Grid when a background refresh fails. Calling
  // params.fail() would mark the block unavailable and blank out its rows
  // even though the previous data is still valid; replaying it via
  // params.success() instead keeps the grid showing what it already had.
  const lastSuccessByBlockRef = useRef(
    new Map<string, { rows: unknown[]; rowCount?: number }>()
  )

  const getRowsRef = useRef<
    ((params: IServerSideGetRowsParams) => Promise<void>) | null
  >(null)
  getRowsRef.current = async (params: IServerSideGetRowsParams) => {
    const requestEpoch =
      (params.context as GridRequestContext | undefined)?.requestEpoch ??
      getCurrentRequestEpoch()
    const routeKey = JSON.stringify(params.request.groupKeys ?? [])
    const blockKey = `${routeKey}:${params.request.startRow ?? 0}`
    const refreshRun = activeRefreshRunRef.current
    if (refreshRun?.epoch === requestEpoch) {
      refreshRun.requestStarted = true
      refreshRun.requestedRoutes.add(routeKey)
    }
    activeRequestCountRef.current += 1

    const {
      fetchData: currentFetchData,
      sortMapping: currentSortMapping,
      pageSize: currentPageSize,
      errorMessage,
    } = latestOptionsRef.current
    const isRootFirstPage =
      (params.request.groupKeys?.length ?? 0) === 0 &&
      (params.request.startRow ?? 0) === 0
    // Only the very first load owns the overlay; refreshes update silently.
    const isInitialRequest = isInitialLoadRef.current
    if (isRootFirstPage && isInitialRequest) showGridLoading(requestEpoch)

    try {
      const request = params.request

      // Translate ag-grid colIds to backend field names via sortMapping
      const translatedSortModel = (request.sortModel ?? []).map((item) => ({
        ...item,
        colId: currentSortMapping[item.colId] || item.colId,
      }))

      const result = await currentFetchData({
        startRow: request.startRow ?? 0,
        endRow: request.endRow ?? currentPageSize,
        sortModel: translatedSortModel,
        filterModel: request.filterModel ?? {},
        groupKeys: request.groupKeys ?? [],
        rowGroupCols: request.rowGroupCols ?? [],
        parentData: params.parentNode?.data,
      })

      const rows = result.rows
      const lastRow = result.totalCount ?? undefined

      lastSuccessByBlockRef.current.set(blockKey, {
        rows,
        rowCount: lastRow,
      })
      params.success({ rowData: rows, rowCount: lastRow })
      if (isRootFirstPage && isInitialRequest)
        finishGridLoading(rows.length === 0, requestEpoch)

      // AG Grid only emits StoreRefreshedEvent when it had rows marked for
      // refresh. A route that was empty at refresh start has nothing marked,
      // so no event fires even when this response brings new rows (e.g. the
      // first idea created on an empty list). That leaves the refresh run
      // stuck and blocks all subsequent reload/refresh calls. Proactively
      // clear the pending route in those cases so finishRefresh can proceed.
      if (
        refreshRun?.epoch === requestEpoch &&
        refreshRun.intent === 'refresh' &&
        (rows.length === 0 || refreshRun.emptyRoutes.has(routeKey))
      ) {
        refreshRun.pendingRoutes.delete(routeKey)
      }
    } catch {
      // A background refresh (not the initial load) failing should not blank
      // out rows the user is already looking at. params.fail() would mark the
      // block unavailable and clear its cells, so replay the last known-good
      // response for this block instead when one exists.
      const staleBlock = lastSuccessByBlockRef.current.get(blockKey)
      if (!isRootFirstPage || !isInitialRequest) {
        if (staleBlock) {
          params.success({
            rowData: staleBlock.rows,
            rowCount: staleBlock.rowCount,
          })
        } else {
          params.fail()
        }
        markBackgroundFetchError(requestEpoch)
      } else {
        params.fail()
        showGridError(errorMessage, requestEpoch)
      }
      const currentRun = activeRefreshRunRef.current
      if (currentRun?.epoch === requestEpoch) currentRun.failed = true
    } finally {
      activeRequestCountRef.current -= 1
      if (
        isRootFirstPage &&
        isInitialRequest &&
        isCurrentRequestEpoch(requestEpoch)
      ) {
        isInitialLoadRef.current = false
        setIsInitialLoad(false)
      }
      finishRefreshRef.current()
      scheduleQueuedRefresh()
    }
  }

  const serverSideDatasourceRef = useRef<IServerSideDatasource | null>(null)
  if (!serverSideDatasourceRef.current) {
    serverSideDatasourceRef.current = {
      getRows: (params) => void getRowsRef.current?.(params),
    }
  }

  const finishRefresh = useCallback(() => {
    const run = activeRefreshRunRef.current
    // AG Grid only re-fetches stores inside the viewport, so off-screen
    // expanded routes never emit storeRefreshed; wait on requested ones only.
    const hasOutstandingRoute =
      run?.intent === 'refresh' &&
      [...run.pendingRoutes].some((route) => run.requestedRoutes.has(route))
    if (
      !run ||
      !run.requestStarted ||
      hasOutstandingRoute ||
      activeRequestCountRef.current > 0 ||
      hasLoadingGridBlocks()
    )
      return

    activeRefreshRunRef.current = null

    if (queuedRefreshIntentRef.current) {
      scheduleQueuedRefresh()
      return
    }

    if (!run.failed && isCurrentRequestEpoch(run.epoch)) {
      latestOptionsRef.current.onRefreshed?.()
    }
  }, [hasLoadingGridBlocks, isCurrentRequestEpoch, scheduleQueuedRefresh])
  finishRefreshRef.current = finishRefresh

  const handleStoreRefreshed = useCallback(
    (event: StoreRefreshedEvent) => {
      const run = activeRefreshRunRef.current
      if (!run || run.intent === 'reload' || !isCurrentRequestEpoch(run.epoch))
        return

      const routeKey = JSON.stringify(event.route ?? [])
      if (!run.requestedRoutes.has(routeKey)) return
      run.pendingRoutes.delete(routeKey)

      // A refresh can change the store structure (e.g. a query change collapses
      // or removes group levels), so some routes recorded at refresh start may
      // no longer exist and would never emit storeRefreshed. Prune pending
      // routes down to those still loaded, otherwise the refresh stays "running"
      // forever and coalesces every later reload into a queued no-op.
      const api = gridApiRef.current
      if (api && run.pendingRoutes.size > 0) {
        const liveRoutes = new Set(
          api
            .getServerSideGroupLevelState()
            .map(({ route }) => JSON.stringify(route ?? []))
        )
        for (const route of [...run.pendingRoutes]) {
          if (!liveRoutes.has(route)) run.pendingRoutes.delete(route)
        }
      }

      finishRefresh()
    },
    [finishRefresh, gridApiRef, isCurrentRequestEpoch]
  )

  const startRefresh = useCallback(
    (intent: RefreshIntent) => {
      const api = gridApiRef.current
      if (!api) return

      if (
        isRefreshStartScheduledRef.current ||
        activeRefreshRunRef.current ||
        activeRequestCountRef.current > 0 ||
        hasLoadingGridBlocks() ||
        isInitialLoadRef.current
      ) {
        queuedRefreshIntentRef.current = mergeRefreshIntent(
          queuedRefreshIntentRef.current,
          intent
        )
        return
      }

      const epoch = beginRequestEpoch()

      if (intent === 'reload') {
        activeRefreshRunRef.current = {
          epoch,
          intent,
          failed: false,
          requestStarted: false,
          pendingRoutes: new Set(),
          requestedRoutes: new Set(),
          emptyRoutes: new Set(),
        }
        // A purge means the previous filter/sort's rows no longer apply, so
        // they must not be replayed as a stand-in for the new query.
        lastSuccessByBlockRef.current.clear()
        api.refreshServerSide({ purge: true })
        return
      }

      const levelStates = api.getServerSideGroupLevelState()
      const loadedRoutes = levelStates.map(({ route }) => route)
      const routes = loadedRoutes.length > 0 ? loadedRoutes : [[]]
      const emptyRoutes = new Set(
        levelStates.length > 0
          ? levelStates
              .filter(({ rowCount }) => rowCount === 0)
              .map(({ route }) => JSON.stringify(route ?? []))
          : [JSON.stringify([])]
      )
      activeRefreshRunRef.current = {
        epoch,
        intent,
        failed: false,
        requestStarted: false,
        pendingRoutes: new Set(routes.map((route) => JSON.stringify(route))),
        requestedRoutes: new Set(),
        emptyRoutes,
      }
      routes.forEach((route) => api.refreshServerSide({ route, purge: false }))
    },
    [beginRequestEpoch, gridApiRef, hasLoadingGridBlocks]
  )
  startRefreshRef.current = startRefresh

  const refresh = useCallback(() => startRefresh('refresh'), [startRefresh])
  const reload = useCallback(() => startRefresh('reload'), [startRefresh])

  // Sort triggers AG Grid's own store refresh. Cancel any in-flight app
  // refresh notification so those store events are not mistaken for a
  // manual/auto reload completing.
  const cancelRefreshNotification = useCallback(() => {
    beginRequestEpoch()
    activeRefreshRunRef.current = null
    queuedRefreshIntentRef.current = null
  }, [beginRequestEpoch])

  const handleGridSortChanged = useCallback(
    (event: SortChangedEvent) => {
      cancelRefreshNotification()
      handleSortChanged(event)
    },
    [cancelRefreshNotification, handleSortChanged]
  )

  // Filter also triggers AG Grid's own store refresh, so cancel any in-flight
  // app refresh notification before forwarding to the consumer, otherwise the
  // resulting store events could be mistaken for a manual/auto reload.
  const handleGridFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      cancelRefreshNotification()
      onFilterChanged?.(event)
    },
    [cancelRefreshNotification, onFilterChanged]
  )

  // endregion

  // region Imperative Handle

  const setAllGroupsExpanded = useCallback(
    (expanded: boolean) => {
      groupsOpenByDefaultRef.current = expanded
      toggledGroupKeysRef.current.clear()
      // Per-node setExpanded misses rows whose open-by-default result AG Grid
      // already cached (e.g. seen under a previous query); expandAll does not.
      const api = gridApiRef.current
      if (!api) return
      if (expanded) api.expandAll()
      else api.collapseAll()
    },
    [gridApiRef]
  )

  useImperativeHandle(
    ref,
    () => ({
      refresh,
      reload,
      getGridApi: () => gridApiRef.current,
      setAllGroupsExpanded,
    }),
    [refresh, reload, gridApiRef, setAllGroupsExpanded]
  )

  // endregion

  // region Auto-Refresh

  useGridAutoRefresh(refresh, autoRefreshInterval)

  // endregion

  // region Preserve Expanded Groups Across Sort/Refresh

  // SSRM recreates group nodes on sort/refresh and creates new ones as blocks
  // load. A group opens when its key's state differs from the default, so both
  // per-row toggles and expand/collapse all survive rebuilds and later blocks.
  const groupsOpenByDefaultRef = useRef(false)
  const toggledGroupKeysRef = useRef<Set<string>>(new Set())

  const handleRowGroupOpened = useCallback((event: RowGroupOpenedEvent) => {
    const key = event.node.key
    if (key == null) return
    if (event.expanded === groupsOpenByDefaultRef.current) {
      toggledGroupKeysRef.current.delete(key)
    } else {
      toggledGroupKeysRef.current.add(key)
    }
  }, [])

  // The context menu's "Expand/Collapse All Row Groups" bypasses
  // setAllGroupsExpanded, so sync the default here for both entry points.
  const onAllGroupsExpandedChangeRef = useRef(onAllGroupsExpandedChange)
  onAllGroupsExpandedChangeRef.current = onAllGroupsExpandedChange

  const handleExpandOrCollapseAll = useCallback(
    (event: ExpandOrCollapseAllEvent) => {
      const expanded = event.source === 'expandAll'
      groupsOpenByDefaultRef.current = expanded
      toggledGroupKeysRef.current.clear()
      onAllGroupsExpandedChangeRef.current?.(expanded)
    },
    []
  )

  const isServerSideGroupOpenByDefault = useCallback(
    (params: IsServerSideGroupOpenByDefaultParams) => {
      const key = params.rowNode.key
      const toggled = key != null && toggledGroupKeysRef.current.has(key)
      return groupsOpenByDefaultRef.current !== toggled
    },
    []
  )

  // endregion

  return (
    <IdeyaAgGridTable
      initialState={initialState}
      columnDefs={resolvedColumnDefs}
      defaultColDef={defaultColDef}
      rowModelType={'serverSide'}
      serverSideDatasource={serverSideDatasourceRef.current}
      serverSideSortAllLevels={true}
      cacheBlockSize={pageSize}
      maxBlocksInCache={maxBlocksInCache}
      serverSideInitialRowCount={serverSideInitialRowCount}
      onGridReady={handleGridReady}
      onColumnMoved={handleColumnChanged}
      onColumnPinned={handleColumnChanged}
      onColumnVisible={handleColumnChanged}
      onColumnResized={handleColumnResized}
      onSortChanged={handleGridSortChanged}
      onFilterChanged={handleGridFilterChanged}
      onModelUpdated={handleModelUpdated}
      onStoreRefreshed={handleStoreRefreshed}
      getContextMenuItems={getContextMenuItems}
      getRowStyle={getRowStyle}
      rowClassRules={rowClassRules}
      autoGroupColumnDef={autoGroupColumnDef}
      getRowId={getRowId}
      isServerSideGroup={isServerSideGroup}
      getServerSideGroupKey={getServerSideGroupKey}
      isServerSideGroupOpenByDefault={isServerSideGroupOpenByDefault}
      ssrmExpandAllAffectsAllRows={true}
      onRowGroupOpened={handleRowGroupOpened}
      onExpandOrCollapseAll={handleExpandOrCollapseAll}
      loading={isInitialLoad}
      height={isMobile ? 400 : height}
      className={className}
      overlayNoRowsTemplate={DEFAULT_NO_ROWS_TEMPLATE}
      overlayLoadingTemplate={'<span>Loading...</span>'}
      loadingCellRenderer={(params: ICellRendererParams) => {
        // Only show loading text for child level (level >= 1)
        // Group level uses the overlay instead
        if (params.node.level === 0) return null
        return <span>Loading...</span>
      }}
    />
  )
}

SSRMGrid.displayName = 'SSRMGrid'
